import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Mail, MapPin, Phone } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleWhatsApp = () => {
    window.open(companyInfo.whatsappLink, '_blank');
  };

  const handleCall = () => {
    window.location.href = companyInfo.callLink;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Received!",
      description: "Thank you for contacting us. We'll get back to you within 24 hours.",
    });
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#0f1419] pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Touch</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Have questions about your financial future? We're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 bg-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div>
              <h2 className="text-4xl font-bold text-white mb-8">
                Contact Information
              </h2>
              <div className="space-y-6">
                <Card className="bg-[#1a1f2e] border-gray-800">
                  <CardContent className="p-6 flex items-start">
                    <Mail className="w-8 h-8 text-[#F59E0B] mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-white">Email</h3>
                      <a 
                        href={`mailto:${companyInfo.email}`}
                        className="text-[17px] text-gray-300 hover:text-[#F59E0B] break-all block mb-1"
                      >
                        {companyInfo.email}
                      </a>
                      <a 
                        href={`mailto:${companyInfo.email2}`}
                        className="text-[17px] text-gray-300 hover:text-[#F59E0B] break-all block"
                      >
                        {companyInfo.email2}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#1a1f2e] border-gray-800">
                  <CardContent className="p-6 flex items-start">
                    <Phone className="w-8 h-8 text-[#F59E0B] mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-white">Phone</h3>
                      <button
                        onClick={handleWhatsApp}
                        className="text-[17px] text-gray-300 hover:text-[#F59E0B] block mb-1"
                      >
                        WhatsApp: {companyInfo.whatsapp}
                      </button>
                      <button
                        onClick={handleCall}
                        className="text-[17px] text-gray-300 hover:text-[#F59E0B] block"
                      >
                        Call: {companyInfo.callNumber}
                      </button>
                      <p className="text-sm text-gray-500 mt-2">Available across India</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#1a1f2e] border-gray-800">
                  <CardContent className="p-6 flex items-start">
                    <MapPin className="w-8 h-8 text-[#F59E0B] mr-4 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-white">Office Address</h3>
                      <p className="text-[17px] text-gray-300 leading-relaxed">
                        {companyInfo.address}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-[#F59E0B]/10 to-[#FBBF24]/10 border-[#F59E0B]/30">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-3 text-white">AMFI Registered</h3>
                    <p className="text-[17px] text-gray-300 mb-2">ARN: {companyInfo.arn}</p>
                    <p className="text-sm text-gray-400">
                      Compliant mutual fund distributor adhering to SEBI regulations
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="bg-[#1a1f2e] border-2 border-[#F59E0B]/30">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-white mb-6">
                    Send us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-[17px] text-gray-300">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2 h-12 text-[17px] bg-[#0f1419] border-gray-700 text-white"
                        placeholder="Enter your name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[17px] text-gray-300">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-2 h-12 text-[17px] bg-[#0f1419] border-gray-700 text-white"
                        placeholder="your.email@example.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[17px] text-gray-300">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2 h-12 text-[17px] bg-[#0f1419] border-gray-700 text-white"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-[17px] text-gray-300">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="mt-2 text-[17px] bg-[#0f1419] border-gray-700 text-white"
                        placeholder="Tell us about your financial goals or questions..."
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:from-[#D97706] hover:to-[#F59E0B] text-black font-semibold h-12 text-[17px]"
                    >
                      Send Message
                    </Button>
                    <p className="text-sm text-gray-500 text-center">
                      We typically respond within 24 hours
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Visit Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Office</span>
            </h2>
            <p className="text-xl text-gray-400">
              C402, Aarohi Housing Society, Sus Road, Sus, Pune – 411021
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <Card className="bg-[#1a1f2e] border-gray-800 overflow-hidden">
              <div className="aspect-video">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3782.823576839204!2d73.75838931489813!3d18.547813487407755!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2bf0d8f8e7d7d%3A0x7d7d7d7d7d7d7d7d!2sSus%2C%20Pune%2C%20Maharashtra%20411021!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                ></iframe>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Quick Contact CTA */}
      <section className="py-20 bg-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Prefer to Talk Directly?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Connect with us instantly for immediate assistance
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                onClick={handleCall}
                size="lg"
                className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:from-[#D97706] hover:to-[#F59E0B] text-black font-semibold px-8 py-6 text-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
              <Button
                onClick={handleWhatsApp}
                size="lg"
                className="bg-transparent border-2 border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 font-semibold px-8 py-6 text-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                WhatsApp Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <Card className="max-w-4xl mx-auto bg-[#1a1f2e]/50 border-gray-800">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-gray-400 leading-relaxed">
                <strong className="text-white">Disclaimer:</strong> {companyInfo.disclaimer}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Contact;